import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { updateOrganization } from '../../utils/api/organization';
import Button from '../../componentsV2/Button/Button';
import { redirectToOrganization } from '../../utils/navigation';
import TextInput from '../../componentsV2/FormInputs/TextInput/TextInput';
import TextArea from '../../componentsV2/FormInputs/TextArea/TextArea';
import { useToasts, useCloseDialogWithUnsavedChanges, useUnsavedChangesWarning } from '../../hooks';
import CustomSelect from '../../componentsV2/FormInputs/CustomSelect/CustomSelect';
import PagesHeader from '../../componentsV2/Headers/PagesHeader';

const OrganizationForm = ({ organization, isSuperadmin = false }) => {
  const [organizationData, setOrganizationData] = useState({ ...organization });
  const originalDataRef = useRef({ ...organization }); // Using useRef instead of state
  const [isNameValid, setIsNameValid] = useState(true);
  const [isShortNameValid, setIsShortNameValid] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const { showToast } = useToasts();

  const hasUnsavedChanges = !_.isEqual(originalDataRef.current, organizationData);
  useEffect(() => {
    setIsUnsavedChanges(hasUnsavedChanges);
  }, [hasUnsavedChanges, organizationData]); // organizationData - track changes to organizationData for unsaved changes if isUnsavedChanges was set to false on previous changes

  useUnsavedChangesWarning(isUnsavedChanges);

  const handleCloseDialog = useCloseDialogWithUnsavedChanges({
    isUnsavedChanges: hasUnsavedChanges,
    setIsUnsavedChanges,
    redirectCallback: () => redirectToOrganization(organization.id),
  });

  const validateInputs = () => {
    let isValid = true;

    if (!organizationData.name) {
      setIsNameValid(false);
      isValid = false;
    }

    if (!organizationData.code) {
      setIsShortNameValid(false);
      isValid = false;
    }

    return isValid;
  };

  const onSave = async () => {
    if (!validateInputs()) return;

    setIsSaving(true);

    try {
      const payload = {
        name: organizationData.name,
        code: organizationData.code,
        contact: organizationData.contact,
        contact_email: organizationData.contact_email,
        description: organizationData.description,
        ...(isSuperadmin && { status: organizationData.status }),
      };

      const updatedOrganization = await updateOrganization(organization.id, payload);

      setOrganizationData(updatedOrganization.organization);
      originalDataRef.current = updatedOrganization.organization; // Update ref instead of state
      setIsUnsavedChanges(false);
      showToast({ message: 'Organization updated successfully!', type: 'success' });

      setTimeout(() => {
        redirectToOrganization(organization.id);
      }, 1000);
    } catch (error) {
      showToast({ message: 'Failed to update the organization. Please try again.', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const statusOptions = [
    { label: 'Trial account', value: 0 },
    { label: 'Full account', value: 1 },
  ];

  return (
    <div className="w-[80%]">
      <div className="flex flex-col items-center mt-2">
        <div className="flex justify-between items-center w-full border-b border-vamos-blue-gray pb-2">
          <PagesHeader name="organization" label="Organization" size="h-10" wrapDivClass="py-0" />
          <div className="flex space-x-2">
            <Button
              label="Cancel"
              size="md"
              color="secondary"
              onClick={() => {
                handleCloseDialog();
              }}
            />
            <Button
              label="Save"
              size="md"
              className="px-6"
              isDisabled={isSaving}
              onClick={onSave}
            />
          </div>
        </div>
        <div className="w-full text-left mt-4 space-y-6">
          <TextInput
            isRequired
            label="Name"
            name="organizationName"
            value={organizationData.name}
            setValue={(value) => {
              setOrganizationData((prev) => ({ ...prev, name: value }));
              setIsNameValid(true);
            }}
            className="w-full md:w-1/2 h-10"
            customLabelClasses="text-lg font-medium"
            isValid={isNameValid}
            errorMessage="Include the organization’s name to save changes."
            spaceY="space-y-2"
            fontSize="text-sm"
          />
          <TextInput
            isRequired
            label="Short Name"
            name="organizationShortName"
            value={organizationData.code}
            setValue={(value) => {
              setOrganizationData((prev) => ({ ...prev, code: value }));
              setIsShortNameValid(true);
            }}
            className="w-full md:w-1/2 h-10"
            customLabelClasses="text-lg font-medium"
            isValid={isShortNameValid}
            spaceY="space-y-2"
            errorMessage="Include the organization’s short name to save changes"
            fontSize="text-sm"
          />
          <TextInput
            label="Contact"
            name="organizationContact"
            value={organizationData.contact}
            setValue={(value) => {
              setOrganizationData((prev) => ({ ...prev, contact: value }));
            }}
            className="w-full md:w-1/2 h-10"
            customLabelClasses="text-lg font-medium"
            fontSize="text-sm"
          />
          <TextInput
            label="Contact Email"
            name="organizationContactEmail"
            value={organizationData.contact_email}
            setValue={(value) => {
              setOrganizationData((prev) => ({ ...prev, contact_email: value }));
            }}
            className="w-full md:w-1/2 h-10"
            customLabelClasses="text-lg font-medium"
            spaceY="space-y-2"
            fontSize="text-sm"
          />
          <TextArea
            label="Description"
            name="organizationDescription"
            value={organizationData.description}
            setValue={(value) => {
              setOrganizationData((prev) => ({ ...prev, description: value }));
            }}
            className="w-full md:w-1/2"
            customLabelClasses="text-lg font-medium"
            spaceY="space-y-2"
            fontSize="text-sm"
          />
          {isSuperadmin && (
            <div className="w-full md:w-1/2 h-10">
              <CustomSelect
                label="Status"
                options={statusOptions}
                value={
                  statusOptions.find(
                    (opt) => opt.value === Number(organizationData.status),
                  )?.label || ''
                }
                setValue={(option) => {
                  setOrganizationData((prev) => ({
                    ...prev,
                    status: option?.value,
                  }));
                }}
                spaceY="space-y-2"
                minHeight={40}
                inputHeight="fit-content"
                labelFontSize="text-lg font-medium"
                fontSize={14}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

OrganizationForm.propTypes = {
  organization: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    code: PropTypes.string,
    contact: PropTypes.string,
    contact_email: PropTypes.string,
    description: PropTypes.string,
    status: PropTypes.number,
  }).isRequired,
  isSuperadmin: PropTypes.bool,
};

export default OrganizationForm;
